import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from "../config";
import { toast } from 'react-toastify';
import {
  FaSpinner, FaTruck, FaClipboardList,
  FaArrowRight, FaSyncAlt, FaInfoCircle, FaCalendarAlt, FaSearch
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

const NAVY = '#0A1929';
const GREEN = '#00A86B';

const apiFetch = async (url) => {
  const res = await fetch(`${API_URL}${url}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error((data && data.error) || `Request failed: ${res.status}`);
  return data;
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(`${v}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ALLOCATION_LABELS = {
  whole_job: { text: 'Whole job', color: '#3B82F6' },
  lines_only: { text: 'Per-line', color: '#7C3AED' },
  whole_job_and_lines: { text: 'Whole job + lines', color: '#0EA5E9' }
};

// ============================================
// ADMIN SCHEDULER — pick a subbie, see every job allocated to them, then schedule.
// ============================================
// This exists because some subcontractors have asked us to manage their scheduling entirely
// and will never log in. It is NOT a reimplementation: choosing a job opens the SAME
// scheduler screen the subbie uses, in admin mode (?admin=true&subbieId=…), so the grid, the
// drag-and-drop and the save behaviour are identical by construction.
//
// The job list is the UNION of both ways a job can be allocated to a subbie:
//   * whole-job  → a job_requirements row (accepted job / awarded tender)
//   * per-line   → job_line_items.subcontractor_id (which is how most jobs are allocated,
//                  because every line has its own collection date)
// A per-line job previously appeared in NO subbie-facing list at all.
// ============================================
function AdminScheduler({ onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [subbies, setSubbies] = useState([]);
  const [subbieId, setSubbieId] = useState(searchParams.get('subbie') || '');
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => { loadSubbies(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (subbieId) loadJobs(subbieId); }, [subbieId]);

  const loadSubbies = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/subcontractors');
      const list = data.subcontractors || [];
      setSubbies(list);
      if (!subbieId && list.length > 0) setSubbieId(list[0].id);
    } catch (err) {
      console.error('Error loading subcontractors:', err);
      toast.error(err.message || 'Failed to load subcontractors');
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async (id) => {
    setLoadingJobs(true);
    try {
      const data = await apiFetch(`/admin/subbie-jobs/${id}`);
      setJobs(data.jobs || []);
      setCounts(data.counts || {});
    } catch (err) {
      console.error('Error loading subbie jobs:', err);
      setJobs([]);
      setCounts({});
      toast.error(err.message || 'Failed to load the jobs for this subbie');
    } finally {
      setLoadingJobs(false);
    }
  };

  const selectSubbie = (id) => {
    setSubbieId(id);
    setSearchParams(id ? { subbie: id } : {});
  };

  // Admin mode: the exact same scheduler, told which subbie it is acting for.
  // The PATH matters. `/subbie-scheduler/:jobId` is the subbie's own route and is wrapped in
  // allowedRoles={['subcontractor','subbie']}, so sending an admin there made ProtectedRoute
  // bounce them to the dashboard — the "Schedule fleet does nothing" bug. The admin route is
  // `/admin-scheduler/schedule/:jobId` (same component, admin guard), and `admin=true` is kept
  // because that is how SchedulerShell knows to render the admin layout rather than the subbie one.
  const openScheduler = (jobId) => {
    navigate(`/admin-scheduler/schedule/${jobId}?admin=true&subbieId=${subbieId}`);
  };

  const activeSubbie = subbies.find((s) => s.id === subbieId);
  const term = search.trim().toLowerCase();
  const filtered = jobs.filter((j) => !term || [j.job_number, j.commodity, j.origin, j.destination]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(term)));

  if (loading) {
    return (
      <AdminLayout onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <FaSpinner className="animate-spin text-3xl" style={{ color: GREEN }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onLogout={onLogout}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Subbie Scheduling</h1>
            <p className="text-sm" style={{ color: '#64748B' }}>
              Schedule a subcontractor's fleet on their behalf — the same tool they use, no login required.
            </p>
          </div>
          <button onClick={() => loadJobs(subbieId)} disabled={loadingJobs || !subbieId}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: NAVY }}>
            <FaSyncAlt className={loadingJobs ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-5" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[260px]">
              <label className="block text-xs font-medium uppercase tracking-wide mb-1" style={{ color: '#64748B' }}>Subcontractor</label>
              <select value={subbieId} onChange={(e) => selectSubbie(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none"
                style={{ borderColor: '#E8ECEF' }}>
                <option value="">Select a subcontractor…</option>
                {subbies.map((s) => <option key={s.id} value={s.id}>{s.company_name}</option>)}
              </select>
            </div>
            <div className="min-w-[200px] flex-1">
              <label className="block text-xs font-medium uppercase tracking-wide mb-1" style={{ color: '#64748B' }}>Search jobs</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Job number, commodity or route…"
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none" style={{ borderColor: '#E8ECEF' }} />
              </div>
            </div>
            {activeSubbie && (
              <div className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: '#F0FDF9', color: '#065F46' }}>
                <span className="font-semibold">{counts.jobs || 0}</span> job(s) allocated
                <span className="opacity-70"> · {counts.lines_only || 0} per-line · {counts.whole_job || 0} whole-job</span>
              </div>
            )}
          </div>
          <p className="text-xs mt-3 flex items-start gap-2" style={{ color: '#64748B' }}>
            <FaInfoCircle className="mt-0.5 flex-shrink-0" />
            <span>
              Scheduling writes straight to the subbie's fleet — no acceptance step and no login needed. They see the
              same loads in their portal afterwards.
            </span>
          </p>
        </div>

        {loadingJobs ? (
          <div className="flex items-center justify-center h-64">
            <FaSpinner className="animate-spin text-3xl" style={{ color: GREEN }} />
          </div>
        ) : !subbieId ? (
          <p className="text-sm" style={{ color: '#64748B' }}>Choose a subcontractor to see their allocated jobs.</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border p-8 text-center shadow-sm" style={{ borderColor: '#E8ECEF' }}>
            <p className="text-sm" style={{ color: '#64748B' }}>
              {jobs.length === 0
                ? 'No jobs are allocated to this subcontractor yet. Allocate line items to them in New Jobs first.'
                : 'No jobs match your search.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((j) => {
              const badge = ALLOCATION_LABELS[j.allocation_type] || ALLOCATION_LABELS.whole_job;
              return (
                <div key={j.job_id} className="bg-white rounded-xl border shadow-sm p-5 flex flex-col" style={{ borderColor: '#E8ECEF' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold" style={{ color: NAVY }}>{j.job_number || 'Job'}</h3>
                      <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>{j.commodity || 'Commodity not set'}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                      style={{ backgroundColor: `${badge.color}1A`, color: badge.color }}>
                      {badge.text}
                    </span>
                  </div>

                  <p className="text-xs mt-3" style={{ color: '#475569' }}>
                    {(j.origin || '—')} → {(j.destination || '—')}
                  </p>

                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs" style={{ color: '#64748B' }}>
                    <p><FaCalendarAlt className="inline mr-1" />Collection: <span className="font-medium">{fmtDate(j.pickup_date || j.start_date)}</span></p>
                    <p><FaCalendarAlt className="inline mr-1" />Delivery: <span className="font-medium">{fmtDate(j.delivery_date || j.estimated_end_date)}</span></p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
                    <span className="inline-flex items-center gap-1" style={{ color: '#475569' }}>
                      <FaClipboardList />
                      {j.lines > 0
                        ? `${j.lines} line(s)${j.line_numbers.length ? ` · L${j.line_numbers.join(', L')}` : ''}`
                        : 'No lines assigned'}
                    </span>
                    <span className="inline-flex items-center gap-1" style={{ color: j.loads_active > 0 ? GREEN : '#64748B' }}>
                      <FaTruck /> {j.loads} load(s){j.loads_active > 0 ? ` · ${j.loads_active} active` : ''}
                    </span>
                    {j.has_requirement && (
                      <span style={{ color: '#64748B' }}>Trucks {j.trucks_assigned || 0}/{j.trucks_required || 0}</span>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t flex items-center justify-between" style={{ borderColor: '#F1F5F9' }}>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F1F5F9', color: '#475569' }}>
                      {String(j.job_status || 'unknown').replace(/_/g, ' ')}
                    </span>
                    <button onClick={() => openScheduler(j.job_id)} disabled={!j.schedulable}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40"
                      style={{ backgroundColor: GREEN }}>
                      Schedule fleet <FaArrowRight size={11} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminScheduler;
